import { Children, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { popoverStyle, usePopover } from './usePopover';

export type SelectOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type SelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  variant?: 'field' | 'pill';
  startIcon?: ReactNode;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  'aria-label'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  /** Legacy `<option>` children — parsed into `options` so old call sites work. */
  children?: ReactNode;
};

/** Reads a legacy `<option>` / `<optgroup>` child tree into option objects. */
function optionsFromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as { value?: unknown; children?: ReactNode; disabled?: boolean; label?: string };
      if (child.type === 'optgroup') {
        walk(props.children);
        return;
      }
      if (child.type !== 'option') return;
      const label = typeof props.children === 'string' || typeof props.children === 'number'
        ? String(props.children)
        : Children.toArray(props.children)
            .filter((part) => typeof part === 'string' || typeof part === 'number')
            .join('');
      out.push({
        value: props.value == null ? label : String(props.value),
        label: label || String(props.value ?? ''),
        disabled: props.disabled,
      });
    });
  };
  walk(children);
  return out;
}

/**
 * Themed listbox replacing the native `<select>` — the OS dropdown cannot be
 * styled and clashes with the portal's palette. The popover is portaled to
 * `document.body` so it escapes card/modal overflow clipping.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  size = 'md',
  variant = 'field',
  startIcon,
  className,
  id,
  children,
  required,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: SelectProps) {
  const generatedId = useId();
  const selectId = id || generatedId;
  const listId = `${selectId}-listbox`;

  const items = useMemo(() => options ?? optionsFromChildren(children), [options, children]);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const typeahead = useRef({ query: '', at: 0 });
  const listRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, popoverRef, position } = usePopover({ open, onClose: close });

  const selectedIndex = items.findIndex((item) => item.value === value);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  // Opening lands the active row on the current selection (or the first row).
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : items.findIndex((item) => !item.disabled));
  }, [open, selectedIndex, items]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const step = (from: number, delta: number) => {
    if (!items.length) return -1;
    let next = from;
    for (let i = 0; i < items.length; i += 1) {
      next = (next + delta + items.length) % items.length;
      if (!items[next].disabled) return next;
    }
    return from;
  };

  const commit = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    onChange?.(item.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => step(current < 0 ? -1 : current, 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => step(current < 0 ? 0 : current, -1));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(step(-1, 1));
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(step(0, -1));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      commit(activeIndex);
      return;
    }
    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const now = Date.now();
      const state = typeahead.current;
      state.query = now - state.at > 800 ? event.key : state.query + event.key;
      state.at = now;
      const query = state.query.toLowerCase();
      const from = activeIndex < 0 ? 0 : activeIndex;
      for (let i = 0; i < items.length; i += 1) {
        const index = (from + (state.query.length > 1 ? 0 : 1) + i) % items.length;
        const item = items[index];
        if (!item.disabled && item.label.toLowerCase().startsWith(query)) {
          setActiveIndex(index);
          return;
        }
      }
    }
  };

  const pill = variant === 'pill';
  const height = pill ? 'h-10' : size === 'sm' ? 'h-9' : 'h-11';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={selectId}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${selectId}-opt-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-required={required}
        className={cx(
          'inline-flex w-full items-center gap-2 text-start transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none',
          height,
          pill
            ? 'rounded-full border border-hairline-strong bg-surface-lowest ps-3.5 pe-2.5 text-body-sm font-medium text-ink hover:bg-surface-low focus:border-accent'
            : 'rounded-md border border-hairline-strong bg-input px-3.5 text-body text-ink focus:border-accent focus:bg-surface-lowest',
          className,
        )}
      >
        {startIcon ? <span className="shrink-0 text-ink-muted">{startIcon}</span> : null}
        <span className={cx('min-w-0 flex-1 truncate', !selected && 'text-ink-faint')}>
          {selected ? selected.label : placeholder || ''}
        </span>
        <ChevronDown size={16} className="shrink-0 text-ink-faint" aria-hidden="true" />
      </button>

      {open
        ? createPortal(
            <div
              ref={popoverRef}
              style={popoverStyle(position)}
              className="max-h-72 overflow-y-auto scroll-thin rounded-md border border-hairline bg-surface-lowest py-1 shadow-lg"
            >
              <div ref={listRef} role="listbox" id={listId} aria-label={ariaLabel} aria-activedescendant={activeIndex >= 0 ? `${selectId}-opt-${activeIndex}` : undefined}>
                {items.map((item, index) => {
                  const isSelected = item.value === value;
                  return (
                    <div
                      key={`${item.value}-${index}`}
                      id={`${selectId}-opt-${index}`}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={item.disabled || undefined}
                      onMouseEnter={() => !item.disabled && setActiveIndex(index)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => commit(index)}
                      className={cx(
                        'flex min-h-[44px] cursor-pointer items-center gap-3 px-3.5 py-2 text-body-sm',
                        item.disabled && 'cursor-not-allowed opacity-50',
                        isSelected
                          ? 'bg-primary-container text-accent-deep'
                          : index === activeIndex && !item.disabled
                            ? 'bg-surface-low text-ink'
                            : 'text-ink',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.label}</span>
                        {item.description ? <span className="block truncate text-caption text-ink-faint">{item.description}</span> : null}
                      </span>
                      {isSelected ? <Check size={16} className="shrink-0" aria-hidden="true" /> : null}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
